import useAnalyticsData from "../hooks/useAnalyticsData";
import { useEffect, useMemo, useState } from "react";
import { analyticsPaths } from "../api/analyticsApi";
import { LoadingState, ErrorState } from "./ApiState";
import { fmtDay, fmtTime, fmtDateTime } from "../utils/periodShared";

// Same colours as the Calendar: green = on time / early, orange = posted by delay, red = delayed, blue = upcoming, yellow = scheduled.
const CATS = [
  { key: "on_time", label: "Posted on Time", color: "#1fac71" },
  { key: "posted_delayed", label: "Posted by Delay", color: "#f97316" },
  { key: "currently_delayed", label: "Not Posted Yet", color: "#e5383b" },
  { key: "upcoming", label: "Upcoming", color: "#3b82f6" },
  { key: "scheduled", label: "Scheduled in Scheduler", color: "#f5b700" },
];
const OTHER = { key: "other", label: "Needs attention", color: "#8f8f8a" };
const TYPES = [["REEL", "Reels"], ["POST", "Posts"], ["CAROUSEL", "Carousels"]];
const TYPE_OPTIONS = [["", "All Content"], ["REEL", "Reel"], ["POST", "Post"], ["CAROUSEL", "Carousel"]];
const PAGE = 15;

/** "1 day, 2 hours and 15 minutes" from milliseconds (same wording as the Calendar). */
function duration(ms) {
  const total = Math.max(0, Math.floor(ms / 60000));
  const d = Math.floor(total / 1440), h = Math.floor((total % 1440) / 60), m = total % 60;
  const parts = [];
  if (d) parts.push(`${d} day${d === 1 ? "" : "s"}`);
  if (h) parts.push(`${h} hour${h === 1 ? "" : "s"}`);
  if (m || !parts.length) parts.push(`${m} minute${m === 1 ? "" : "s"}`);
  return parts.length > 1 ? `${parts.slice(0, -1).join(", ")}${parts.length > 2 ? "," : ""} and ${parts[parts.length - 1]}` : parts[0];
}
const pct = (v) => (v === null || v === undefined ? "N/A" : `${v}%`);
const catOf = (key) => CATS.find((c) => c.key === key) || OTHER;

/** One status row's wording; delayed rows are recomputed from the planned time so they keep counting without a reload. */
function statusText(row, now) {
  if (row.category === "currently_delayed") {
    const ms = now - new Date(row.planned_at).getTime();
    return { label: catOf("currently_delayed").label, delay: ms >= 60000 ? duration(ms) : "less than a minute", color: catOf("currently_delayed").color };
  }
  if (row.category === "posted_delayed") return { label: catOf("posted_delayed").label, delay: duration(row.delay_seconds * 1000), color: catOf("posted_delayed").color };
  return { label: row.category === "other" ? row.status.label : catOf(row.category).label, delay: null, color: catOf(row.category).color };
}

function Card({ label, value, color, hint }) {
  return (
    <div className="ou-card" style={{ borderTopColor: color }} title={hint || ""}>
      <div className="ou-card-value">{value}</div>
      <div className="ou-card-label">{label}</div>
    </div>
  );
}

function Distribution({ summary }) {
  const rows = [...CATS, ...(summary.other ? [OTHER] : [])];
  const max = Math.max(1, ...rows.map((c) => summary[c.key]));
  return (
    <div className="ou-chart" role="img" aria-label="Posting status distribution">
      <div className="ou-chart-title">Posting status distribution</div>
      {rows.map((c) => (
        <div className="ou-bar-row" key={c.key}>
          <span className="ou-bar-label">{c.label}</span>
          <span className="ou-bar-track"><span className="ou-bar-fill" style={{ width: `${(summary[c.key] / max) * 100}%`, background: c.color }} /></span>
          <span className="ou-bar-count">{summary[c.key]}</span>
        </div>
      ))}
    </div>
  );
}

function Comparison({ byType, focus }) {
  const cats = [...CATS, OTHER];
  const max = Math.max(1, ...TYPES.map(([k]) => byType[k].total));
  return (
    <div className="ou-chart" role="img" aria-label="Content type comparison">
      <div className="ou-chart-title">Content type comparison</div>
      {TYPES.map(([k, label]) => (
        <div className={`ou-stack-row${focus && focus !== k ? " is-dim" : ""}`} key={k}>
          <span className="ou-bar-label">{label}</span>
          <span className="ou-bar-track">
            {byType[k].total === 0 ? <span className="ou-empty-bar">No content</span> : cats.map((c) => byType[k][c.key] > 0 && (
              <span key={c.key} className="ou-seg" style={{ width: `${(byType[k][c.key] / max) * 100}%`, background: c.color }}
                title={`${label}: ${c.label} ${byType[k][c.key]}`}>{byType[k][c.key]}</span>
            ))}
          </span>
          <span className="ou-bar-count">{byType[k].total}</span>
        </div>
      ))}
      <div className="ou-legend">{cats.filter((c) => byType.REEL[c.key] + byType.POST[c.key] + byType.CAROUSEL[c.key] > 0).map((c) => (
        <span key={c.key}><i style={{ background: c.color }} />{c.label}</span>))}</div>
    </div>
  );
}

/** Analytics > Overall Content Updates: how consistently planned (Calendar) content was published. */
export default function OverallUpdates({ accountId }) {
  const [month, setMonth] = useState("current");
  const [type, setType] = useState("");
  const [now, setNow] = useState(() => Date.now());
  const [page, setPage] = useState(1);
  const [dir, setDir] = useState("desc");

  const report = useAnalyticsData(accountId ? analyticsPaths.updates(accountId,month,type) : null, 30000);
  const { data, error, loading } = report;
  const load = report.refresh;
  useEffect(() => { setPage(1); }, [accountId,month,type]);

  // While anything is still pending (delayed / upcoming / scheduled): re-read the persisted statuses every minute, and
  // advance the delay counters every 30 s from the planned time (no Instagram call, no page reload).
  const pending = data ? data.summary.currently_delayed + data.summary.upcoming + data.summary.scheduled : 0;
  useEffect(() => {
    if (!pending) return undefined;
    const tick = setInterval(() => setNow(Date.now()), 30000);
    const poll = setInterval(() => { if (document.visibilityState === "visible") load(true); }, 60000);
    return () => { clearInterval(tick); clearInterval(poll); };
  }, [pending, load]);

  const sorted = useMemo(() => {
    const rows = [...(data?.rows || [])];
    rows.sort((a, b) => (a.planned_at < b.planned_at ? -1 : a.planned_at > b.planned_at ? 1 : 0) * (dir === "asc" ? 1 : -1));
    return rows;
  }, [data, dir]);
  const pages = Math.max(1, Math.ceil(sorted.length / PAGE));
  const view = sorted.slice((page - 1) * PAGE, page * PAGE);
  const tz = data?.timezone;
  const s = data?.summary;
  const r = data?.rates;

  return (
    <section className="metrics-section ou-section" id="overall-content-updates">
      <div className="metrics-table-head">
        <h3 className="metrics-h3" style={{ margin: 0 }}>Overall content updates</h3>
        <div className="metrics-table-filters">
          <select className="select-filter" aria-label="Month" title="Filter by planned month" value={data ? data.month : "current"} onChange={(e) => setMonth(e.target.value)}>
            {!data && <option value="current">This Month</option>}
            {(data?.months || []).map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
            <option value="all">All Months</option>
          </select>
          <select className="select-filter" aria-label="Content type" title="Filter by content type" value={type} onChange={(e) => setType(e.target.value)}>
            {TYPE_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
      </div>

      {report.refreshing && data && <div className="metrics-caption" role="status">Refreshing...</div>}
      {report.notice && <div className="metrics-caption" role="status">{report.notice}</div>}
      {error && data && <div className="metrics-note">Showing last available content updates. {error}</div>}
      {data?._cache?.cached_at && <div className="metrics-caption">Report cached {fmtDateTime(data._cache.cached_at, data.timezone)}</div>}
      {loading && !data ? <LoadingState label="Loading content updates..." /> : error && !data ? <ErrorState title="Unable to load content updates" message={error} onRetry={() => load(false)} /> : data && (
        s.total === 0 ? (
          <div className="metrics-note">No planned content for {data.month === "all" ? "any month" : data.months.find((m) => m.key === data.month)?.label || data.month}{type ? ` (${TYPE_OPTIONS.find(([k]) => k === type)[1]})` : ""}. Add content in the Calendar to see how it performs.</div>
        ) : (
          <>
            <div className="ou-cards">
              <Card label="Total Planned Content" value={s.total} color="#0e0e0e" hint="Unique Calendar items planned in the selected period" />
              {CATS.map((c) => <Card key={c.key} label={c.label} value={s[c.key]} color={c.color} />)}
            </div>
            {s.other > 0 && <div className="metrics-caption">{s.other} item{s.other === 1 ? "" : "s"} need attention (for example a failed Scheduler job); they count in the total and appear in the table.</div>}

            <div className="ou-rates">
              <div><strong>{pct(r.on_time_rate)}</strong><span>Posted on Time</span></div>
              <div><strong>{pct(r.late_rate)}</strong><span>{catOf("posted_delayed").label}</span></div>
            </div>


            <div className="ou-charts">
              <Distribution summary={s} />
              <Comparison byType={data.by_type} focus={type} />
            </div>

            <div className="metrics-table-wrap" style={{ marginTop: 18 }}>
              <table className="metrics-table an-center ou-table">
                <thead><tr>
                  <th>Content Type</th><th>Caption</th>
                  <th aria-sort={dir === "asc" ? "ascending" : "descending"}><button type="button" className="metrics-sort-btn" onClick={() => { setDir(dir === "asc" ? "desc" : "asc"); setPage(1); }}>Planned Date{dir === "asc" ? " ▲" : " ▼"}</button></th>
                  <th>Planned Time</th><th>Actual Published At</th><th>Posting Status</th><th>Delay Duration</th><th>Posting Method</th>
                </tr></thead>
                <tbody>{view.map((row) => {
                  const st = statusText(row, now);
                  return (
                    <tr key={row.key}>
                      <td>{row.kind_label}</td>
                      <td title={row.caption} className="ou-caption">{row.caption || "—"}</td>
                      <td>{fmtDay(row.planned_at, tz)}</td>
                      <td>{fmtTime(row.planned_at, tz)}</td>
                      <td>{row.actual_published_at ? fmtDateTime(row.actual_published_at, tz) : "—"}</td>
                      <td><span className="ou-pill" style={{ borderColor: st.color, color: st.color }}>{st.label}</span></td>
                      <td>{st.delay || "—"}</td>
                      <td>{row.method === "SCHEDULER" ? "Instagram Scheduler" : "Direct Instagram"}</td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </div>
            {sorted.length > PAGE && (
              <div className="metrics-pager">
                <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
                <span>Page {page} of {pages}</span>
                <button className="btn btn-ghost btn-sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</button>
              </div>
            )}
          </>
        )
      )}
    </section>
  );
}
